<script lang="ts">
import { SelectItemType } from '@/app/common/types/selectItem.type';
import { DPM_STATUS, DPM_STATUS_OPTIONS } from '@/app/const';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import AirlineAnswer from '../shared/airlineAnswer.vue';
import DpmAnswer from '../shared/dpmAnswer.vue';
import ComplaintChat from '../shared/chat.vue';

export default {
    components: {
        DpmAnswer,
        AirlineAnswer,
        ComplaintChat,
    },
    setup() {
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
    },
    emits: [
        'update:modelValue',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            panel: [] as string[],
            dpmStatuses: DPM_STATUS_OPTIONS as SelectItemType[],
            dpmStatus: DPM_STATUS,
            displayChat: false,
            selectedDepartmentCode: undefined as string | undefined,
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal) {
                this.model = newVal;
                this.refreshPanel();
            }
        },
    },
    computed: {
        haveMainDepartment(): boolean {
            return this.model.relatedMainDepartment !== undefined
                && this.model.relatedMainDepartment.departmentCode !== undefined
                && this.model.relatedMainDepartment.departmentCode !== null;
        },
    },
    async mounted() {
        this.refreshPanel();
    },
    methods: {
        collapse(value: string) {
            if (this.panel)
                this.panel = this.panel.filter(o => o != value);
        },
        expand(value: string) {
            if (this.panel)
                this.panel.push(value);
        },
        dpmColor(status: string | undefined) {
            return this.dpmStatuses.find(o => o.value === status)?.color;
        },
        dpmStatusLabel(status: string | undefined) {
            const label = this.dpmStatuses.find(o => o.value === status)?.label;
            return label ? this.$t(`t-${label}`) : '';
        },
        refreshPanel() {
            // set expand panel
            let mainDpm = this.model.relatedMainDepartment?.departmentCode;
            if (mainDpm && !this.panel.includes(mainDpm)) this.panel.push(mainDpm);
        },
        openDpmChat(departmentCode: string | undefined) {
            this.selectedDepartmentCode = departmentCode;
            this.displayChat = true;
        },
    }
}
</script>
<template>
    <v-card v-if="haveMainDepartment" class="app-card admin-card">
        <v-toolbar density="compact">
            <v-toolbar-title>{{ $t('t-complaint-dpm-answer-back') }}</v-toolbar-title>
        </v-toolbar>
        <v-divider></v-divider>
        <v-card-text>
            <v-expansion-panels v-if="panel" v-model="panel" class="admin-card" multiple>
                <v-expansion-panel v-if="model.relatedMainDepartment"
                    :value="model.relatedMainDepartment.departmentCode">
                    <v-expansion-panel-title class="py-3" readonly static>
                        <div class="d-flex align-center pr-4">
                            <v-toolbar-title>{{ $t('t-complaint-main-responsible-dpm') }}</v-toolbar-title>
                        </div>
                        <div class="splitter mr-3 my-1"></div>
                        <span class="mr-3">{{ $t('t-department') }} {{ model.relatedMainDepartment.departmentCode }}</span>
                        <v-chip v-if="dpmStatusLabel(model.relatedMainDepartment.departmentStatus)" variant="flat"
                            :color="dpmColor(model.relatedMainDepartment.departmentStatus)">
                            {{ dpmStatusLabel(model.relatedMainDepartment.departmentStatus) }}
                        </v-chip>
                        <div v-if="model.relatedMainDepartment?.external" class="mx-3 d-flex align-center">
                            <v-icon icon="mdi mdi-forum-outline" size="small" class="mr-2"></v-icon>
                            <span class="mr-2">{{ $t('t-complaint-chat') }}</span>
                            <v-icon icon="mdi mdi-chevron-right"
                                @click="openDpmChat(model.relatedMainDepartment.departmentCode)"></v-icon>
                        </div>
                        <template v-slot:actions="{ collapseIcon, expandIcon }">
                            <v-icon v-if="panel.includes(model.relatedMainDepartment.departmentCode)" :icon="expandIcon"
                                @click="collapse(model.relatedMainDepartment.departmentCode)"></v-icon>
                            <v-icon v-else :icon="collapseIcon"
                                @click="expand(model.relatedMainDepartment.departmentCode)"></v-icon>
                        </template>
                    </v-expansion-panel-title>
                    <v-divider class="mx-6"></v-divider>
                    <v-expansion-panel-text class="px-1 py-3">
                        <!-- answer from department -->
                        <DpmAnswer v-model="model.relatedMainDepartment" :view-mode="true"></DpmAnswer>
                        <!-- answer from airline -->
                        <div v-if="model.relatedMainDepartment.external && model.relatedMainDepartment.external.externalDepartmentAnswer
                            && (model.relatedMainDepartment?.departmentStatus === dpmStatus.ACCEPTED
                                || model.relatedMainDepartment?.departmentStatus === dpmStatus.END)"
                            class="mt-4">
                            <AirlineAnswer v-model="model.relatedMainDepartment.external" :complaint="model">
                            </AirlineAnswer>
                        </div>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="model.relatedSubDepartments"
                    v-for="(dpm, index) in model.relatedSubDepartments" :value="dpm.departmentCode">
                    <v-expansion-panel-title class="py-3" readonly static>
                        <div class="d-flex align-center pr-4">
                            <v-toolbar-title>
                                {{ $t('t-complaint-sub-responsible-dpm') }}
                                <span v-if="model.relatedSubDepartments.length > 1">
                                    {{ $t('t-at') }} {{ index + 1}}
                                </span>
                            </v-toolbar-title>
                        </div>
                        <div class="splitter mr-3 my-1"></div>
                        <span class="mr-3">{{ $t('t-department') }} {{ dpm.departmentCode }}</span>
                        <v-chip v-if="dpmStatusLabel(dpm.departmentStatus)" variant="flat"
                            :color="dpmColor(dpm.departmentStatus)">
                            {{ dpmStatusLabel(dpm.departmentStatus) }}
                        </v-chip>
                        <div v-if="dpm?.external" class="mx-3 d-flex align-center">
                            <v-icon icon="mdi mdi-forum-outline" size="small" class="mr-2"></v-icon>
                            <span class="mr-2">{{ $t('t-complaint-chat') }}</span>
                            <v-icon icon="mdi mdi-chevron-right" @click="openDpmChat(dpm.departmentCode)"></v-icon>
                        </div>
                        <template v-slot:actions="{ collapseIcon, expandIcon }">
                            <v-icon v-if="panel.includes(dpm.departmentCode)" :icon="expandIcon"
                                @click="collapse(dpm.departmentCode)"></v-icon>
                            <v-icon v-else :icon="collapseIcon" @click="expand(dpm.departmentCode)"></v-icon>
                        </template>
                    </v-expansion-panel-title>
                    <v-divider class="mx-6"></v-divider>
                    <v-expansion-panel-text class="px-1 py-3">
                        <!-- answer from department -->
                        <DpmAnswer v-model="model.relatedSubDepartments[index]" :view-mode="true"></DpmAnswer>
                        <!-- answer from airline -->
                        <AirlineAnswer
                            v-if="model.relatedSubDepartments[index].external && model.relatedSubDepartments[index].external.externalDepartmentAnswer
                            && (model.relatedSubDepartments[index]?.departmentStatus === dpmStatus.ACCEPTED
                                || model.relatedSubDepartments[index]?.departmentStatus === dpmStatus.END)"
                            v-model="model.relatedSubDepartments[index].external" :complaint="model">
                        </AirlineAnswer>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card-text>
    </v-card>
    <ComplaintChat v-model="displayChat" :complaint="model" :is-owner="false" :waitAirline="false"
        :view-mode="true" :filter-department-code="selectedDepartmentCode">
    </ComplaintChat>
</template>